import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { ProspectStage } from '@bits/shared/zod';
import { formatInr, paise } from '@bits/domain';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { stageBadgeVariant, stageLabel } from './sales.prospects';

export const salesPipelineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sales/pipeline',
  component: SalesPipelinePage,
});

const OPEN_STAGES: readonly ProspectStage[] = ['lead', 'contacted', 'proposal_sent', 'negotiation'];

function SalesPipelinePage(): JSX.Element {
  const openQuery = useQuery({
    queryKey: ['sales-pipeline', 'open'],
    queryFn: () => api.listProspects({ status: 'active', pageSize: 100, page: 1 }),
  });
  const wonQuery = useQuery({
    queryKey: ['sales-pipeline', 'won'],
    queryFn: () => api.listProspects({ stage: 'won', pageSize: 10, page: 1 }),
  });
  const lostQuery = useQuery({
    queryKey: ['sales-pipeline', 'lost'],
    queryFn: () => api.listProspects({ stage: 'lost', pageSize: 10, page: 1 }),
  });

  const openItems = openQuery.data?.items ?? [];
  const columns: Record<ProspectStage, typeof openItems> = {
    lead: [],
    contacted: [],
    proposal_sent: [],
    negotiation: [],
    won: [],
    lost: [],
  };
  for (const p of openItems) {
    if (OPEN_STAGES.includes(p.stage)) columns[p.stage].push(p);
  }

  return (
    <AppShell>
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">Pipeline</h1>
        <p className="text-base text-[hsl(var(--muted-foreground))]">
          Open prospects by stage. Click a card to open its detail.
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        {OPEN_STAGES.map((stage) => {
          const items = columns[stage];
          const totalPaise = items.reduce(
            (acc, i) => acc + BigInt(i.estimatedValuePaise ?? 0),
            0n,
          );
          return (
            <Card key={stage} className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{stageLabel(stage)}</CardTitle>
                  <Badge variant={stageBadgeVariant(stage)}>{items.length}</Badge>
                </div>
                <CardDescription className="text-xs">
                  {totalPaise > 0n ? formatInr(paise(totalPaise)) : '—'}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                {items.length === 0 ? (
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Empty</p>
                ) : (
                  items.map((p) => (
                    <Link
                      key={p.id}
                      to="/sales/prospects/$prospectId"
                      params={{ prospectId: p.id }}
                      className="block rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-3 text-sm shadow-sm transition-colors hover:bg-[hsl(var(--accent))]/40"
                    >
                      <div className="font-medium">{p.displayName}</div>
                      <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
                        {p.ownerName}
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs">
                        <span className="tabular-nums">
                          {p.estimatedValuePaise !== null
                            ? formatInr(paise(BigInt(p.estimatedValuePaise)))
                            : '—'}
                        </span>
                        <span
                          className={
                            p.nextTouchDate && p.nextTouchDate < new Date().toISOString().slice(0, 10)
                              ? 'text-[hsl(var(--destructive))]'
                              : 'text-[hsl(var(--muted-foreground))]'
                          }
                        >
                          {p.nextTouchDate ?? 'no touch'}
                        </span>
                      </div>
                      {p.isStale ? (
                        <Badge variant="destructive" className="mt-2">
                          stale
                        </Badge>
                      ) : null}
                    </Link>
                  ))
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <RecentPanel title="Recently Won" items={wonQuery.data?.items ?? []} />
        <RecentPanel title="Recently Lost" items={lostQuery.data?.items ?? []} />
      </div>
    </AppShell>
  );
}

function RecentPanel({
  title,
  items,
}: {
  title: string;
  items: ReadonlyArray<{
    id: string;
    displayName: string;
    ownerName: string;
    estimatedValuePaise: number | null;
    stage: ProspectStage;
  }>;
}): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Nothing yet.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
            {items.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <div>
                  <Link
                    to="/sales/prospects/$prospectId"
                    params={{ prospectId: p.id }}
                    className="font-medium"
                  >
                    {p.displayName}
                  </Link>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{p.ownerName}</div>
                </div>
                <div className="text-right tabular-nums">
                  {p.estimatedValuePaise !== null
                    ? formatInr(paise(BigInt(p.estimatedValuePaise)))
                    : '—'}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
